import React, { useState, useRef, useEffect, useCallback } from 'react';

const SliderField = ({
                         value = 50,
                         min = 0,
                         max = 100,
                         step = 1,
                         changeFn = () => {},
                         name = '',
                         orientation = 'vertical',
                         className = '',
                     }) => {
    const [sliderValue, setSliderValue] = useState(value);
    const [isDragging, setIsDragging] = useState(false);
    const [dimensions, setDimensions] = useState({ width: 200, height: 200 });
    const sliderRef = useRef(null);
    const handleRef = useRef(null);

    // Update internal value when prop changes
    useEffect(() => {
        setSliderValue(value);
    }, [value]);

    // ResizeObserver to track container size changes
    useEffect(() => {
        if (!sliderRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setDimensions({ width, height });
            }
        });

        resizeObserver.observe(sliderRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Calculate value from position
    const calculateValueFromPosition = useCallback((clientX, clientY) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        let percentage;

        if (orientation === 'vertical') {
            // For vertical slider without rotation
            // Bottom = min value, Top = max value
            const trackHeight = rect.height - 30; // Account for margins
            const relativeY = rect.bottom - clientY - 15; // Distance from bottom
            percentage = Math.max(0, Math.min(1, relativeY / trackHeight));
        } else {
            // For horizontal slider
            const trackWidth = rect.width - 30; // Account for margins
            const relativeX = clientX - rect.left - 15;
            percentage = Math.max(0, Math.min(1, relativeX / trackWidth));
        }

        const range = max - min;
        let newValue = min + percentage * range;

        // Round to step
        newValue = Math.round(newValue / step) * step;
        newValue = Math.max(min, Math.min(max, newValue));

        return newValue;
    }, [orientation, min, max, step]);

    // Handle mouse down on handle
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        handleRef.current?.focus();
    };

    // Handle mouse move
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            const newValue = calculateValueFromPosition(e.clientX, e.clientY);
            setSliderValue(newValue);
            changeFn(newValue, name);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, calculateValueFromPosition, changeFn, name]);

    // Handle touch events for mobile
    const handleTouchStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        handleRef.current?.focus();
    };

    useEffect(() => {
        const handleTouchMove = (e) => {
            if (!isDragging) return;

            const touch = e.touches[0];
            const newValue = calculateValueFromPosition(touch.clientX, touch.clientY);
            setSliderValue(newValue);
            changeFn(newValue, name);
        };

        const handleTouchEnd = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, calculateValueFromPosition, changeFn, name]);

    // Handle clicks on track
    const handleTrackClick = (e) => {
        if (e.target === handleRef.current || handleRef.current?.contains(e.target)) {
            return; // Ignore clicks on handle
        }

        const newValue = calculateValueFromPosition(e.clientX, e.clientY);
        setSliderValue(newValue);
        changeFn(newValue, name);
        handleRef.current?.focus();
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        let newValue = sliderValue;
        const bigStep = (max - min) / 10;

        if (orientation === 'vertical') {
            switch(e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    newValue = Math.min(max, sliderValue + step);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    newValue = Math.max(min, sliderValue - step);
                    break;
                case 'PageUp':
                    e.preventDefault();
                    newValue = Math.min(max, sliderValue + bigStep);
                    break;
                case 'PageDown':
                    e.preventDefault();
                    newValue = Math.max(min, sliderValue - bigStep);
                    break;
                case 'Home':
                    e.preventDefault();
                    newValue = max;
                    break;
                case 'End':
                    e.preventDefault();
                    newValue = min;
                    break;
            }
        } else {
            switch(e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    newValue = Math.min(max, sliderValue + step);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    newValue = Math.max(min, sliderValue - step);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    newValue = Math.min(max, sliderValue + bigStep);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    newValue = Math.max(min, sliderValue - bigStep);
                    break;
                case 'PageUp':
                    e.preventDefault();
                    newValue = Math.min(max, sliderValue + bigStep);
                    break;
                case 'PageDown':
                    e.preventDefault();
                    newValue = Math.max(min, sliderValue - bigStep);
                    break;
                case 'Home':
                    e.preventDefault();
                    newValue = max;
                    break;
                case 'End':
                    e.preventDefault();
                    newValue = min;
                    break;
            }
        }

        if (newValue !== sliderValue) {
            setSliderValue(newValue);
            changeFn(newValue, name);
        }
    };

    const progress = ((sliderValue - min) / (max - min)) * 100;

    // Calculate handle position in pixels
    const getHandlePosition = () => {
        if (orientation === 'vertical') {
            const trackHeight = dimensions.height - 30; // Account for margins
            const handlePosition = (progress / 100) * trackHeight;
            return { bottom: `${handlePosition + 15 - 7.5}px` };
        } else {
            const trackWidth = dimensions.width - 30; // Account for margins
            const handlePosition = (progress / 100) * trackWidth;
            return { left: `${handlePosition + 15 - 20}px` };
        }
    };

    return (
        <div
            ref={sliderRef}
            className={`SliderField ${orientation === 'vertical' ? 'SliderField--vertical' : 'SliderField--horizontal'} ${isDragging ? 'SliderField--dragging' : ''} ${className}`}
            onClick={handleTrackClick}
        >
            <div className="SliderField__track">
                <div
                    className="SliderField__fill"
                    style={{
                        [orientation === 'vertical' ? 'height' : 'width']: `${progress}%`
                    }}
                />
            </div>
            <div
                ref={handleRef}
                className="SliderField__handle"
                style={getHandlePosition()}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="slider"
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={sliderValue}
                aria-orientation={orientation}
            >
                <div className="SliderField__handle-lines"></div>
            </div>
        </div>
    );
};

export default SliderField;